import React, { useMemo, useState } from "react";
import {
  MDBCardHeader,
  MDBCardTitle,
} from "mdb-react-ui-kit";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spinner, Input, FormAutocomplete } from "../common";
import { Col } from "../App.styles";
import TeamChip from "./TeamChip";
import GroupCardAction from "./GroupCardAction";
import { HeadRow, AddButton, GroupViewBody, GroupViewContent, AddBody } from "./GroupView.style";
import { useTeamsOptionsQuery } from '../teams/Teams.state'
import { useDestroyMutation, useGroupQuery, useUpdateMutation } from './Groups.state'
import { useDebouncedCallback } from 'use-debounce'

const AddToTeam = ({ toggles, onToggle, loading, children }) => {
  const onAddToTeam = () => onToggle('addToTeam')
  const onKeyUp = (e) => {
    if (e.key !== "Enter") return;
    onAddToTeam();
};
  return (
    <>
      {
        toggles.addToTeam
          ? <AddBody>{children}</AddBody>
          : null
      }
      <AddButton disabled={loading} onClick={onAddToTeam} onKeyUp={onKeyUp} tabIndex={0}>
        {
          loading
            ? <Spinner size={20} />
            : <p>{toggles.addToTeam ? "-" : "+"}</p>
        }
      </AddButton>
    </>
  );
};

const TeamSelect = ({ setValue, value, teams: currentTeams = [] }) => {
    const [term, setTerm] = useState('')
    const { data: sourceTeams, isFetching: isLoading } = useTeamsOptionsQuery({ term })
    const { teams = [] } = sourceTeams || {}
    const onRemoteSearch = useDebouncedCallback((query) => {
      setTerm(query)
    }, 600)
  
    return (
      <FormAutocomplete
        loading={isLoading}
        options={
          teams
            .slice()
            .filter(t => !currentTeams.find(team => team.id === t.id))
            .sort(({ name }) => name)
        }
        disableClearable={true}
        size="small"
        value={value}
        onChange={(e, v) => setValue(v)}
        onRemoteSearch={onRemoteSearch}
        stateful={false}
      />
    )
};

export default function GroupView(props) {
  const { t } = useTranslation()
  const { 
    id: groupId, 
    label: name, 
    teams: sourceTeams, 
    onEdit, 
    onClose, 
    onToggle,
    toggles,
    editMode, 
    deleteSection,
  } = props
  const { game_id } = useParams();
  const { update, isUpdating } = useUpdateMutation();
  const { destroy, isDestroying } = useDestroyMutation();
  const useQuery = () => useGroupQuery(groupId)
  const [addedTeams, setAddedTeams] = useState([])
  const [removedTeams, setRemovedTeams] = useState([])

  const teams = useMemo(() => {
    return sourceTeams.concat(addedTeams)
        .filter(team => {
            return !removedTeams.find(t => t.id === team?.id)
        })
        // Filter unique teams
        .filter((team, index, array) => {
            return array.findIndex(t => t.id === team?.id) === index
        })
        .sort((a, b) => {
            if (a.id > b.id) {
                return 1
            } else if (a.id < b.id) {
                return -1
            }
            return 0
        })
  }, [sourceTeams, removedTeams, addedTeams])

  const addTeamToGroup = (newTeam) => {
    const { id: teamId } = newTeam
    onToggle('addToTeam')
    update({
      groupId,
      team_id: teamId,
    })
    setAddedTeams([...addedTeams, newTeam])
    setRemovedTeams([...removedTeams.filter(t => t.id !== teamId)])
  };

  const removeTeamFromGroup = (removedTeam) => {
    const { id } = removedTeam
    destroy({
      groupId,
      team_id: id,
      game_id: game_id,
    })
    setRemovedTeams([...removedTeams, removedTeam])
    setAddedTeams([...addedTeams.filter(t => t.id !== id)])
  };

  return (
    <>
      <>
        <MDBCardHeader>
          <HeadRow>
            {!editMode ? (
              <Col>
                <MDBCardTitle>{name}</MDBCardTitle>
              </Col>
            ) : (
              <>
                <Col size="9" sm="10">
                  <Input
                    name="label"
                    label={t("Group Name")}
                    useQuery={useQuery}
                  />
                </Col>
                <Col size="3" sm="2">
                  { teams.length > 0 ? null : deleteSection }
                </Col>
              </>
            )}
          </HeadRow>
        </MDBCardHeader>
        <GroupViewBody>
          <GroupViewContent>
            {teams.map((team, index) => {
              return (
                <TeamChip
                  key={index}
                  thumbnail={team.assets.photos[0].url}
                  name={team.name}
                  editMode={editMode}
                  onDelete={() => removeTeamFromGroup(team)}
                  loading={isDestroying}
                />
              );
            })}
            <AddToTeam
              toggles={toggles}
              onToggle={onToggle}
              loading={isUpdating}
            >
              <div>
                <TeamSelect setValue={addTeamToGroup} teams={teams} />
              </div>
            </AddToTeam>
          </GroupViewContent>
          <GroupCardAction editMode={editMode} onEdit={onEdit} onClose={onClose} />
        </GroupViewBody>
      </>
    </>
  );
}
